import { Box, BoxProps, Grid, Typography } from "@mui/material";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import { Input } from "../../../../../features/talentProfile/editForms/salaries/CustomInputs";
import { Controller, useFormContext } from "react-hook-form";
import { NumberRangeRules, NumberRangeValue } from "./types";

type Props = {
  name: string;
  unit?: string;
  maxWidth?: string;
  BoxProps?: BoxProps;
  CustomInputProps?: Record<string, any>;
  decimalSeparator?: string;
  decimalScale?: number;
  allowNegative?: boolean;
  NumericFormatProps?: NumericFormatProps;
  rules?: NumberRangeRules;
  disableErrorMessage?: boolean;
};

const NumberRange = ({
  name,
  unit,
  maxWidth,
  BoxProps = {},
  decimalSeparator = ",",
  decimalScale = 2,
  CustomInputProps = {},
  allowNegative = false,
  NumericFormatProps = {},
  rules = {},
  disableErrorMessage = true,
  ...restProps
}: Props) => {
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext();

  const numberRangeError = errors[name];
  const errorMessage = numberRangeError?.message?.toString();

  const getErrorFlags = () => {
    if (!numberRangeError) {
      return { leftError: false, rightError: false };
    }
    if (errorMessage?.endsWith("_from")) {
      return { leftError: true, rightError: false };
    }
    if (errorMessage?.endsWith("_to")) {
      return { leftError: false, rightError: true };
    }
    // for any other error
    return { leftError: true, rightError: true };
  };

  const { leftError, rightError } = getErrorFlags();

  // destructure the external validate function from rules if provided
  const { validate: externalValidate, ...otherRules } = rules;

  // check if range is valid as a default validation
  const defaultValidate = (value: NumberRangeValue) => {
    if (value?.from && value?.to && +value?.from > +value?.to) {
      return "Invalid range"; // TODO: translate
    }
    return true;
  };

  const isRequired = (value: NumberRangeValue) => {
    // this is a bit of a hack, for some reason { required: true } is not working as expected
    if (Object.keys(otherRules).includes("required")) {
      const errorMessage = "required"; // TODO: translate

      if (!value?.from && !value?.to) return errorMessage;
      if (!value?.from) return "required_from";
      if (!value?.to) return "required_to";
    }
    return true;
  };

  // run default validation and external validation if provided
  const combinedValidate = (value: NumberRangeValue) => {
    const isRequiredResult = isRequired(value);
    if (isRequiredResult !== true) {
      return isRequiredResult;
    }

    const defaultResult = defaultValidate(value);
    if (defaultResult !== true) {
      return defaultResult;
    }
    if (externalValidate && typeof externalValidate === "function") {
      const externalResult = externalValidate(value, getValues());
      return externalResult;
    }
    return true;
  };

  // create a combined rules object that includes all other rules and uses the combined validate function
  const combinedRules: NumberRangeRules = {
    ...otherRules,
    validate: (value) => combinedValidate(value),
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        width="100%"
        maxWidth={maxWidth || "unset"}
        justifyContent="space-between"
        {...BoxProps}
      >
        <Controller
          name={name}
          control={control}
          defaultValue={{ from: "", to: "" }}
          rules={combinedRules}
          render={({ field: { value, onChange } }) => (
            <>
              <NumericFormat
                {...NumericFormatProps}
                allowNegative={allowNegative}
                value={value?.from || ""}
                decimalSeparator={decimalSeparator}
                onValueChange={(values) =>
                  onChange({ ...value, from: values.value })
                }
                customInput={Input}
                unit={unit}
                decimalScale={decimalScale}
                error={!!leftError}
                inputProps={{
                  style: { textAlign: "center" },
                  ...CustomInputProps?.inputProps,
                }}
                {...CustomInputProps}
                {...restProps}
              />
              <Typography>-</Typography>
              <NumericFormat
                {...NumericFormatProps}
                allowNegative={allowNegative}
                value={value?.to || ""}
                decimalSeparator={decimalSeparator}
                onValueChange={(values) =>
                  onChange({ ...value, to: values.value })
                }
                customInput={Input}
                unit={unit}
                decimalScale={decimalScale}
                error={!!rightError}
                inputProps={{
                  style: {
                    textAlign: "center",
                    ...CustomInputProps?.inputProps,
                  },
                }}
                {...CustomInputProps}
                {...restProps}
              />
            </>
          )}
        />
      </Box>
      {/* this might need adjustments to work with different layouts  */}
      {!disableErrorMessage && numberRangeError && errorMessage && (
        <Grid xs={12} ml="14px">
          <Typography color="error" fontSize="12px" letterSpacing="0.03333em">
            {/* TODO: show translated error messages */}
            {errorMessage}
          </Typography>
        </Grid>
      )}
    </>
  );
};

export default NumberRange;
